import React, { useEffect, useState } from 'react';
import BalanceCard from '../components/BalanceCard';
import { apiService } from '../services/api';
import { useAuth } from '../hooks/useAuth';

interface Leader {
  place: number;
  user_id: number;
  username: string;
  monthly_income: number;
}

interface LeadersResponse {
  leaders: Leader[];
  user_info?: Leader | null;
}

const Leaders: React.FC = () => {
  const { user } = useAuth();
  const [leaders, setLeaders] = useState<Leader[]>([]);
  const [userInfo, setUserInfo] = useState<Leader | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaders = async () => {
      setLoading(true);
      try {
        const data: LeadersResponse = await apiService.getMonthlyLeaders(50);
        setLeaders(data.leaders);
        setUserInfo(data.user_info || null);
      } catch (e) {
        setLeaders([]);
        setUserInfo(null);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaders();
  }, []);

  return (
    <div className="px-8 py-8 bg-[#181A20] min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-wide">Лидеры</h1>
          <p className="text-gray-400 mt-2 text-sm">Топ пользователей по доходу за текущий месяц</p>
        </div>
      </div>

      {/* Топ-3 карточки */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        {[0, 1, 2].map((idx) => {
          const leader = leaders[idx];
          let textColor = '';
          let dustColorSet: string[] = [];
          if (idx === 0) {
            textColor = 'text-yellow-400';
            dustColorSet = ['#facc15', '#fde047', '#eab308']; // gold
          } else if (idx === 1) {
            textColor = 'text-gray-300';
            dustColorSet = ['#d1d5db', '#f3f4f6', '#9ca3af']; // silver
          } else if (idx === 2) {
            textColor = 'text-orange-400';
            dustColorSet = ['#fb923c', '#fdba74', '#ea580c']; // bronze
          }
          return (
            <BalanceCard
              key={idx}
              title={`${idx + 1} место`}
              value={leader ? <span className={textColor}>{leader.username} — {leader.monthly_income.toFixed(2)} ₽</span> : <span className={textColor}>—</span>}
              showDust={true}
              dustColorSet={dustColorSet}
            />
          );
        })}
      </div>

      {/* Таблица лидеров */}
      <div className="bg-[#181A20] rounded-xl shadow border border-[#23262F]">
        <div className="px-6 py-4 border-b border-[#23262F]">
          <h2 className="text-xl font-semibold text-white">TOP-50</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#23262F]">
            <thead className="bg-[#181A20]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#B1B5C3] uppercase tracking-wider">№</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#B1B5C3] uppercase tracking-wider">Никнейм</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#B1B5C3] uppercase tracking-wider">Доход за месяц</th>
              </tr>
            </thead>
            <tbody className="bg-[#181A20] divide-y divide-[#23262F]">
              {leaders.map((leader) => (
                <tr key={leader.user_id} className={user?.id === leader.user_id ? 'bg-[#23262F]' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap text-white font-bold">{leader.place}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-white">{leader.username}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-green-400 font-semibold">{leader.monthly_income.toFixed(2)} ₽</td>
                </tr>
              ))}
              {/* Если пользователь не в топ-50, показываем его место отдельно */}
              {userInfo && (
                <tr className="bg-[#23262F]">
                  <td className="px-6 py-4 whitespace-nowrap text-white font-bold">{userInfo.place}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-white">{userInfo.username} (Вы)</td>
                  <td className="px-6 py-4 whitespace-nowrap text-green-400 font-semibold">{userInfo.monthly_income.toFixed(2)} ₽</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}
    </div>
  );
};

export default Leaders; 